/**
 * Phase 1.7 — OnboardingNudge.
 *
 * Inline tip strip — appears above a section to walk a new user through
 * their first action ("Add your first animal", "Connect your first
 * calendar", etc.). Dismissible, with the dismissed-id persisted to
 * `ui_prefs.onboarding.dismissed[]`.
 *
 * Spec note: the audit's yellow background does not work in dark mode.
 * We use surface-raised + clay border accent in both modes; the icon
 * carries the warm tone instead of the bg.
 */

import { Lightbulb, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useUiPrefs } from '@/lib/layout-flag';

export interface OnboardingNudgeProps {
  id: string;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function OnboardingNudge({ id, title, description, action, className }: OnboardingNudgeProps): JSX.Element | null {
  const { prefs, patch } = useUiPrefs();
  const dismissed = prefs.onboarding?.dismissed ?? [];
  if (dismissed.includes(id)) return null;

  const dismiss = (): void => {
    void patch({
      onboarding: { ...(prefs.onboarding ?? {}), dismissed: [...dismissed, id] },
    });
  };

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 rounded-xl border-l-4 border-clay bg-surface-raised px-4 py-3',
        'dark:bg-surface-raised-dark dark:border-clay-dark',
        className,
      )}
      role="status"
    >
      <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-clay dark:text-clay-dark" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-charcoal dark:text-charcoal-dark">{title}</p>
        {description ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        ) : null}
        {action ? <div className="mt-3">{action}</div> : null}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-charcoal dark:hover:text-charcoal-dark"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
